import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Refresh,
  PlayArrow,
  Language,
  Restaurant,
  Visibility,
  Photo,
  InsertDriveFile,
  Replay,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import TabDescription from './TabDescription';
import 'firebase/firestore';
import 'firebase/storage';
import axios from 'axios';
import { useRegionContext } from '../../region/RegionContext';

const PAGE_SIZE = 25;

const CvMenuReview = () => {
  const { db, endpoints } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingMenus, setPendingMenus] = useState([]);
  const [page, setPage] = useState(0);
  const [expandedVenue, setExpandedVenue] = useState(null);
  const [dishes, setDishes] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [deleteOnReject, setDeleteOnReject] = useState(false);
  const [reprocessDialog, setReprocessDialog] = useState(null); // menu object
  const [selectedReprocessImages, setSelectedReprocessImages] = useState([]); // URLs
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);

  const venueGroups = useMemo(() => {
    const groups = {};
    pendingMenus.forEach(menu => {
      if (!groups[menu.businessId]) {
        groups[menu.businessId] = {
          businessId: menu.businessId,
          bizName: menu.bizName,
          bizAddress: menu.bizAddress,
          bizWeb: menu.bizWeb,
          bizPhone: menu.bizPhone,
          bizImages: menu.bizImages,
          menuImageResolvedUrls: menu.menuImageResolvedUrls,
          pdfMenuStoragePath: menu.pdfMenuStoragePath,
          pdfMenuResolvedUrl: menu.pdfMenuResolvedUrl,
          menus: [],
          totalDishes: 0,
        };
      }
      groups[menu.businessId].menus.push(menu);
      groups[menu.businessId].totalDishes += menu.dishCount;
    });
    return Object.values(groups);
  }, [pendingMenus]);

  useEffect(() => {
    loadPendingMenus();
  }, []);

  const loadPendingMenus = async () => {
    try {
      setLoading(true);
      setError(null);

      setPage(0);

      // Load pending CV-extracted menus
      const menusSnap = await db
        .collection('MenuDetails')
        .where('cvVerificationStatus', '==', 'pending')
        .limit(500)
        .get();

      const menus = [];

      for (const doc of menusSnap.docs) {
        const data = doc.data();

        // Count linked dishes
        const dishSnap = await db
          .collection('DishDetails')
          .where('dishMenuId', '==', data.menuId)
          .get();

        // Fetch business details
        let bizName = '';
        let bizWeb = '';
        let bizAddress = '';
        let bizPhone = '';
        let bizImages = [];
        let menuImageStoragePaths = [];
        let pdfMenuStoragePath = '';
        if (data.menuBizId) {
          const bizDoc = await db.collection('Business').doc(data.menuBizId).get();
          if (bizDoc.exists) {
            const bizData = bizDoc.data();
            bizName = bizData.bizName || '';
            bizWeb = bizData.bizWeb || '';
            bizAddress = bizData.bizAddress || '';
            bizPhone = bizData.bizPhone || '';
            bizImages = bizData.images || [];
            menuImageStoragePaths = bizData.menuImageStoragePaths || [];
            pdfMenuStoragePath = bizData.pdfMenuStoragePath || '';
          }
        }

        // Resolve storage paths to fresh download URLs
        const storage = firebase.storage();
        const menuImageResolvedUrls = await Promise.all(
          menuImageStoragePaths.map(path =>
            storage.ref(path).getDownloadURL().catch(() => null)
          )
        );
        const pdfMenuResolvedUrl = pdfMenuStoragePath
          ? await storage.ref(pdfMenuStoragePath).getDownloadURL().catch(() => null)
          : null;

        menus.push({
          menuId: data.menuId,
          businessId: data.menuBizId,
          bizName,
          bizWeb,
          bizAddress,
          bizPhone,
          bizImages,
          menuImageStoragePaths,
          menuImageResolvedUrls: menuImageResolvedUrls.filter(Boolean),
          pdfMenuStoragePath,
          pdfMenuResolvedUrl,
          menuName: data.menuName || 'Unnamed Menu',
          menuDesc: data.menuDesc || '',
          dishCount: dishSnap.size,
          extractedAt: data.cvExtractedAt,
          cvSource: data.cvSource || 'gpt-4o-vision',
          cvSourceImageUrls: data.cvSourceImageUrls || [],
        });
      }

      // Sort by extraction date (newest first)
      menus.sort((a, b) => {
        const aTime = a.extractedAt?.toDate?.()?.getTime?.() || 0;
        const bTime = b.extractedAt?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });

      setPendingMenus(menus);
    } catch (err) {
      console.error('Error loading pending menus:', err);
      setError('Failed to load pending menus');
    } finally {
      setLoading(false);
    }
  };

  const loadDishes = async (menuId) => {
    if (dishes[menuId]) return; // Already loaded

    try {
      const dishSnap = await db
        .collection('DishDetails')
        .where('dishMenuId', '==', menuId)
        .get();

      const dishList = dishSnap.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          name: d.dishName || '',
          description: d.dishDesc || '',
          price: d.dishPrice1 || 0,
          category: d.scrapedCategory || '',
          options: d.dishOptions || [],
          extras: d.dishExtras || [],
          sizes: d.dishAvailSize || [],
        };
      });

      setDishes((prev) => ({ ...prev, [menuId]: dishList }));
    } catch (err) {
      console.error('Error loading dishes:', err);
    }
  };

  const toggleVenue = (businessId) => {
    if (expandedVenue === businessId) {
      setExpandedVenue(null);
    } else {
      setExpandedVenue(businessId);
      const group = venueGroups.find(g => g.businessId === businessId);
      if (group) {
        group.menus.forEach(m => loadDishes(m.menuId));
      }
    }
  };

  const handleApprove = async (menu) => {
    if (!window.confirm(`Approve "${menu.menuName}" for ${menu.bizName}? This will make the menu and all ${menu.dishCount} dishes live on the app.`)) {
      return;
    }

    try {
      setActionLoading(menu.menuId);
      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();

      await axios.post(
        endpoints.adminVerifyMenu,
        {
          action: 'approve',
          businessId: menu.businessId,
          menuId: menu.menuId,
        },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      // Remove from list
      setPendingMenus((prev) => prev.filter((m) => m.menuId !== menu.menuId));
    } catch (err) {
      console.error('Approve failed:', err);
      alert(`Failed to approve: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    const menu = rejectDialog;
    if (!menu) return;

    try {
      setActionLoading(menu.menuId);
      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();

      await axios.post(
        endpoints.adminVerifyMenu,
        {
          action: 'reject',
          businessId: menu.businessId,
          menuId: menu.menuId,
          deleteOnReject,
        },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      // Remove from list
      setPendingMenus((prev) => prev.filter((m) => m.menuId !== menu.menuId));
      setRejectDialog(null);
      setDeleteOnReject(false);
    } catch (err) {
      console.error('Reject failed:', err);
      alert(`Failed to reject: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkProcess = async () => {
    if (!window.confirm('Run CV extraction on all unprocessed venues with stored menu files? This may take several minutes.')) {
      return;
    }

    try {
      setBulkProcessing(true);
      setBulkResult(null);
      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();

      const response = await axios.post(
        endpoints.bulkProcessMenuCV,
        { limit: 50 },
        {
          headers: { Authorization: `Bearer ${idToken}` },
          timeout: 600000, // 10 minute timeout for bulk
        }
      );

      setBulkResult(response.data.summary);
      // Reload the pending list
      await loadPendingMenus();
    } catch (err) {
      console.error('Bulk processing failed:', err);
      alert(`Bulk processing failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleApproveAll = async (group) => {
    if (!window.confirm(`Approve all ${group.menus.length} menu sections for ${group.bizName}? This will make ${group.totalDishes} dishes live on the app.`)) {
      return;
    }
    try {
      setActionLoading(group.businessId);
      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();
      for (const menu of group.menus) {
        await axios.post(
          endpoints.adminVerifyMenu,
          {
            action: 'approve',
            businessId: menu.businessId,
            menuId: menu.menuId,
          },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
      }
      const menuIds = new Set(group.menus.map(m => m.menuId));
      setPendingMenus((prev) => prev.filter((m) => !menuIds.has(m.menuId)));
    } catch (err) {
      console.error('Approve all failed:', err);
      alert(`Failed to approve all: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openReprocessDialog = (menu) => {
    // Collect all available image URLs for selection (source images + business images)
    setReprocessDialog(menu);
    setSelectedReprocessImages([]);
    setReprocessResult(null);
  };

  const toggleReprocessImage = (url) => {
    setSelectedReprocessImages((prev) => {
      if (prev.includes(url)) return prev.filter((u) => u !== url);
      if (prev.length >= 5) return prev; // max 5
      return [...prev, url];
    });
  };

  const handleReprocess = async () => {
    if (selectedReprocessImages.length === 0) {
      alert('Please select at least one file to re-run CV on.');
      return;
    }

    try {
      setReprocessing(true);
      setReprocessResult(null);
      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();

      const response = await axios.post(
        endpoints.adminVerifyMenu,
        {
          action: 'reprocess',
          businessId: reprocessDialog.businessId,
          menuId: reprocessDialog.menuId,
          imageUrls: selectedReprocessImages,
        },
        {
          headers: { Authorization: `Bearer ${idToken}` },
          timeout: 300000, // 5 minute timeout
        }
      );

      setReprocessResult(response.data);

      // Reload the pending list and clear cached dishes so they refresh
      setDishes((prev) => {
        const next = { ...prev };
        delete next[reprocessDialog.menuId];
        return next;
      });
      await loadPendingMenus();
    } catch (err) {
      console.error('Reprocess failed:', err);
      alert(`Reprocess failed: ${err.response?.data?.message || err.response?.data?.error || err.message}`);
    } finally {
      setReprocessing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading pending CV menus...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            CV Menu Review
          </Typography>
          <TabDescription>
            Menus extracted by GPT-4o Vision awaiting approval before going live. Data comes from the
            MenuDetails collection (cvVerificationStatus: pending) with linked dishes from DishDetails and
            images from Firebase Storage. Review extracted menu items for accuracy against the original
            images/PDFs, then approve to publish or reject to discard. Use "Run Bulk CV" to process
            multiple venues at once.
          </TabDescription>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPendingMenus}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={bulkProcessing ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            onClick={handleBulkProcess}
            disabled={bulkProcessing}
            size="small"
          >
            {bulkProcessing ? 'Processing...' : 'Run Bulk CV'}
          </Button>
        </Box>
      </Box>

      {/* Bulk process result */}
      {bulkResult && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setBulkResult(null)}>
          Bulk processing complete: {bulkResult.processed} processed, {bulkResult.failed} failed,
          {' '}{bulkResult.menusCreated} menus and {bulkResult.dishesCreated} dishes created.
        </Alert>
      )}

      {/* Summary stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {pendingMenus.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
                {pendingMenus.reduce((sum, m) => sum + m.dishCount, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Dishes Extracted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                {new Set(pendingMenus.map((m) => m.businessId)).size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Venues with Pending Menus
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Pending venues table */}
      {venueGroups.length === 0 ? (
        <Alert severity="info">
          No pending CV menus to review. Menus will appear here automatically when files are
          uploaded to Firebase Storage, or you can click "Run Bulk CV" to process stored files.
        </Alert>
      ) : (
        <>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button variant="outlined" size="small" disabled={page === 0} onClick={() => { setPage(p => p - 1); setExpandedVenue(null); }}>
            Previous
          </Button>
          <Typography variant="body2">
            Page {page + 1} of {Math.ceil(venueGroups.length / PAGE_SIZE)} ({venueGroups.length} venues)
          </Typography>
          <Button variant="outlined" size="small" disabled={(page + 1) * PAGE_SIZE >= venueGroups.length} onClick={() => { setPage(p => p + 1); setExpandedVenue(null); }}>
            Next
          </Button>
        </Box>
        <Card>
          <Table aria-label="Pending venue reviews">
            <TableHead>
              <TableRow>
                <TableCell scope="col" />
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Venue</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Website</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }} align="center">Menus</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }} align="center">Dishes</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Extracted</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {venueGroups.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((group) => (
                <React.Fragment key={group.businessId}>
                  <TableRow hover>
                    <TableCell sx={{ width: 40 }}>
                      <IconButton
                        size="small"
                        aria-label={expandedVenue === group.businessId ? 'Collapse venue' : 'Expand venue'}
                        aria-expanded={expandedVenue === group.businessId}
                        onClick={() => toggleVenue(group.businessId)}
                      >
                        {expandedVenue === group.businessId ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          <Restaurant sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />
                          {group.bizName || 'Unknown Venue'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.bizAddress}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {group.bizWeb ? (
                        <Tooltip title={group.bizWeb}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={group.bizWeb}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'primary.main' }}
                          >
                            <Language sx={{ fontSize: 14 }} />
                            {new URL(group.bizWeb).hostname.replace('www.', '')}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`${group.menus.length} section${group.menus.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={group.totalDishes} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(group.menus.reduce((latest, m) => {
                          const t = m.extractedAt?.toDate?.()?.getTime?.() || 0;
                          return t > (latest?.toDate?.()?.getTime?.() || 0) ? m.extractedAt : latest;
                        }, null))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={actionLoading === group.businessId ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                        onClick={() => handleApproveAll(group)}
                        disabled={!!actionLoading}
                      >
                        Approve All ({group.menus.length})
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Expanded venue details */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedVenue === group.businessId ? undefined : 'none' }}>
                      <Collapse in={expandedVenue === group.businessId} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                            {group.bizPhone && (
                              <Typography variant="body2" color="text.secondary">
                                Phone: {group.bizPhone}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              Business ID: <code>{group.businessId}</code>
                            </Typography>
                          </Box>

                          {/* Source images */}
                          {group.menuImageResolvedUrls.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Source Images ({group.menuImageResolvedUrls.length})
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {group.menuImageResolvedUrls.map((url, idx) => (
                                  <Box
                                    key={idx}
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      width: 120,
                                      height: 120,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      border: '1px solid',
                                      borderColor: 'grey.300',
                                      cursor: 'pointer',
                                      '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={url}
                                      alt={`Source image ${idx + 1}`}
                                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;font-size:11px;text-align:center;padding:4px">Image ${idx + 1}<br/>unavailable</div>`;
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Business images */}
                          {group.bizImages.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                <Photo sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                All Business Images ({group.bizImages.length})
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {group.bizImages.map((url, idx) => (
                                  <Box
                                    key={idx}
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      width: 90,
                                      height: 90,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      border: '1px solid',
                                      borderColor: 'grey.300',
                                      cursor: 'pointer',
                                      '&:hover': { borderColor: 'info.main', boxShadow: 2 },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={url}
                                      alt={`Business image ${idx + 1}`}
                                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Stored menu files */}
                          {(group.pdfMenuResolvedUrl || group.menuImageResolvedUrls.length > 0) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                <InsertDriveFile sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Stored Menu Files ({(group.pdfMenuResolvedUrl ? 1 : 0) + group.menuImageResolvedUrls.length})
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                {group.pdfMenuResolvedUrl && (
                                  <Chip
                                    icon={<InsertDriveFile />}
                                    label={group.pdfMenuStoragePath.split('/').pop()}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    component="a"
                                    href={group.pdfMenuResolvedUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    clickable
                                  />
                                )}
                                {group.menuImageResolvedUrls.map((url, idx) => (
                                  <Box
                                    key={idx}
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      width: 90,
                                      height: 90,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      border: '1px solid',
                                      borderColor: 'grey.300',
                                      cursor: 'pointer',
                                      '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={url}
                                      alt={`Menu file ${idx + 1}`}
                                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Menu sections */}
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                            Menu Sections ({group.menus.length})
                          </Typography>

                          {group.menus.map((menu) => (
                            <Card key={menu.menuId} variant="outlined" sx={{ mb: 2 }}>
                              <CardContent sx={{ pb: '16px !important' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{menu.menuName}</Typography>
                                    <Chip label={`${menu.dishCount} dishes`} size="small" color="primary" variant="outlined" />
                                    <Chip label={menu.cvSource} size="small" variant="outlined" />
                                    <Typography variant="caption" color="text.secondary">{formatDate(menu.extractedAt)}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Re-run CV on selected files">
                                      <Button variant="outlined" color="info" size="small" startIcon={<Replay />}
                                        onClick={() => openReprocessDialog(menu)} disabled={!!actionLoading}>
                                        Re-run
                                      </Button>
                                    </Tooltip>
                                    <Button variant="contained" color="success" size="small"
                                      startIcon={actionLoading === menu.menuId ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                                      onClick={() => handleApprove(menu)} disabled={!!actionLoading}>
                                      Approve
                                    </Button>
                                    <Button variant="outlined" color="error" size="small" startIcon={<Cancel />}
                                      onClick={() => setRejectDialog(menu)} disabled={!!actionLoading}>
                                      Reject
                                    </Button>
                                  </Box>
                                </Box>

                                {menu.menuDesc && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    {menu.menuDesc}
                                  </Typography>
                                )}

                                {!dishes[menu.menuId] ? (
                                  <Box sx={{ py: 1 }}><CircularProgress size={20} /></Box>
                                ) : dishes[menu.menuId].length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No dishes extracted</Typography>
                                ) : (
                                  <Table size="small" aria-label={`Dishes for ${menu.menuName}`}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell scope="col" sx={{ fontWeight: 600 }}>Dish Name</TableCell>
                                        <TableCell scope="col" sx={{ fontWeight: 600 }}>Description</TableCell>
                                        <TableCell scope="col" sx={{ fontWeight: 600 }}>Category</TableCell>
                                        <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Price</TableCell>
                                        <TableCell scope="col" sx={{ fontWeight: 600 }}>Options</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {dishes[menu.menuId].map((dish) => (
                                        <TableRow key={dish.id}>
                                          <TableCell>
                                            <Typography variant="body2">{dish.name}</Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                              {dish.description || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {dish.category ? (
                                              <Chip label={dish.category} size="small" variant="outlined" />
                                            ) : '—'}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {dish.price ? `$${Number(dish.price).toFixed(2)}` : '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {dish.options.length > 0
                                              ? dish.options.map((opt, i) => (
                                                  <Chip key={i} label={opt} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                                ))
                                              : '—'}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Card>
        </>
      )}
      {/* Reject confirmation dialog */}
      <Dialog open={!!rejectDialog} onClose={() => { setRejectDialog(null); setDeleteOnReject(false); }}>
        <DialogTitle>Reject Menu</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Reject "{rejectDialog?.menuName}" for {rejectDialog?.bizName}?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="deleteOnReject"
              checked={deleteOnReject}
              onChange={(e) => setDeleteOnReject(e.target.checked)}
            />
            <label htmlFor="deleteOnReject">
              <Typography variant="body2">
                Also delete the menu and all extracted dishes from Firestore
              </Typography>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialog(null); setDeleteOnReject(false); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading === rejectDialog?.menuId}
            startIcon={actionLoading === rejectDialog?.menuId ? <CircularProgress size={14} /> : <Cancel />}
          >
            {deleteOnReject ? 'Reject & Delete' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-run CV dialog */}
      <Dialog
        open={!!reprocessDialog}
        onClose={() => { if (!reprocessing) { setReprocessDialog(null); setReprocessResult(null); } }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Re-run CV Extraction</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Select 1-5 files to re-process for <strong>{reprocessDialog?.bizName}</strong> — "{reprocessDialog?.menuName}".
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            GPT-4o will extract dishes from the selected images or PDF. New dishes will be added to the existing menu (duplicates are skipped).
          </Typography>

          {reprocessResult && (
            <Alert severity={reprocessResult.dishesCreated > 0 ? 'success' : 'info'} sx={{ mb: 2 }}>
              {reprocessResult.message}
            </Alert>
          )}

          {/* Stored PDF menu */}
          {reprocessDialog?.pdfMenuResolvedUrl && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                <InsertDriveFile sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Stored Menu PDF
              </Typography>
              <Box
                onClick={() => toggleReprocessImage(reprocessDialog.pdfMenuResolvedUrl)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  border: '3px solid',
                  borderColor: selectedReprocessImages.includes(reprocessDialog.pdfMenuResolvedUrl) ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                  transition: 'all 0.15s',
                  bgcolor: selectedReprocessImages.includes(reprocessDialog.pdfMenuResolvedUrl) ? 'primary.50' : 'grey.50',
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <InsertDriveFile sx={{ color: selectedReprocessImages.includes(reprocessDialog.pdfMenuResolvedUrl) ? 'primary.main' : 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {reprocessDialog.pdfMenuStoragePath?.split('/').pop() || 'Menu PDF'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: selectedReprocessImages.includes(reprocessDialog.pdfMenuResolvedUrl) ? 'primary.main' : 'text.secondary' }}>
                    {selectedReprocessImages.includes(reprocessDialog.pdfMenuResolvedUrl) ? 'Selected' : 'Click to select'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Stored menu images */}
          {reprocessDialog?.menuImageResolvedUrls?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Menu Source Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {reprocessDialog.menuImageResolvedUrls.map((url, idx) => (
                  <Box
                    key={`src-${idx}`}
                    onClick={() => toggleReprocessImage(url)}
                    sx={{
                      width: 140,
                      cursor: 'pointer',
                      border: '3px solid',
                      borderColor: selectedReprocessImages.includes(url) ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      overflow: 'hidden',
                      opacity: selectedReprocessImages.includes(url) ? 1 : 0.7,
                      transition: 'all 0.15s',
                      '&:hover': { opacity: 1, borderColor: 'primary.light' },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={`Source ${idx + 1}`}
                      sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Box sx={{ textAlign: 'center', py: 0.5, bgcolor: selectedReprocessImages.includes(url) ? 'primary.main' : 'grey.100' }}>
                      <Typography variant="caption" sx={{ color: selectedReprocessImages.includes(url) ? 'white' : 'text.secondary' }}>
                        {selectedReprocessImages.includes(url) ? 'Selected' : 'Click to select'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* CV source images (original extraction URLs) */}
          {reprocessDialog?.cvSourceImageUrls?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Original CV Source Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {reprocessDialog.cvSourceImageUrls
                  .filter((url) => !reprocessDialog.menuImageResolvedUrls?.includes(url))
                  .map((url, idx) => (
                    <Box
                      key={`cv-${idx}`}
                      onClick={() => toggleReprocessImage(url)}
                      sx={{
                        width: 140,
                        cursor: 'pointer',
                        border: '3px solid',
                        borderColor: selectedReprocessImages.includes(url) ? 'primary.main' : 'grey.300',
                        borderRadius: 1,
                        overflow: 'hidden',
                        opacity: selectedReprocessImages.includes(url) ? 1 : 0.7,
                        transition: 'all 0.15s',
                        '&:hover': { opacity: 1, borderColor: 'primary.light' },
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt={`CV source ${idx + 1}`}
                        sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Box sx={{ textAlign: 'center', py: 0.5, bgcolor: selectedReprocessImages.includes(url) ? 'primary.main' : 'grey.100' }}>
                        <Typography variant="caption" sx={{ color: selectedReprocessImages.includes(url) ? 'white' : 'text.secondary' }}>
                          {selectedReprocessImages.includes(url) ? 'Selected' : 'Click to select'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Box>
          )}

          {/* Business images (Google Photos) */}
          {reprocessDialog?.bizImages?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Business Images (Google Photos)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {reprocessDialog.bizImages.map((url, idx) => (
                  <Box
                    key={`biz-${idx}`}
                    onClick={() => toggleReprocessImage(url)}
                    sx={{
                      width: 140,
                      cursor: 'pointer',
                      border: '3px solid',
                      borderColor: selectedReprocessImages.includes(url) ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      overflow: 'hidden',
                      opacity: selectedReprocessImages.includes(url) ? 1 : 0.7,
                      transition: 'all 0.15s',
                      '&:hover': { opacity: 1, borderColor: 'primary.light' },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={`Business photo ${idx + 1}`}
                      sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Box sx={{ textAlign: 'center', py: 0.5, bgcolor: selectedReprocessImages.includes(url) ? 'primary.main' : 'grey.100' }}>
                      <Typography variant="caption" sx={{ color: selectedReprocessImages.includes(url) ? 'white' : 'text.secondary' }}>
                        {selectedReprocessImages.includes(url) ? 'Selected' : 'Click to select'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {selectedReprocessImages.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedReprocessImages.length} file{selectedReprocessImages.length !== 1 ? 's' : ''} selected (max 5)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReprocessDialog(null); setReprocessResult(null); }} disabled={reprocessing}>
            {reprocessResult ? 'Close' : 'Cancel'}
          </Button>
          {!reprocessResult && (
            <Button
              variant="contained"
              color="info"
              onClick={handleReprocess}
              disabled={reprocessing || selectedReprocessImages.length === 0}
              startIcon={reprocessing ? <CircularProgress size={14} color="inherit" /> : <Replay />}
            >
              {reprocessing ? 'Processing...' : `Re-run CV on ${selectedReprocessImages.length} file${selectedReprocessImages.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CvMenuReview;
